import { FeatureLabel } from '@/components/marketing/info-tip';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentPage, ContentSection } from '@/components/marketing/content-page';
import { ContentFaq } from '@/components/marketing/content-faq';
import {
  setDocumentMeta, setCanonical, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema, buildTechArticleSchema,
} from '@/lib/seo';

const FAQS = [
  {
    q: 'How can AI help small businesses with customer service?',
    a: 'AI helps small businesses handle customer service with a smaller team by structuring incoming requests automatically (turning emails and chat messages into categorized, prioritized tickets), drafting first replies for agent approval, answering repetitive questions instantly, and summarizing ticket trends in plain language. The practical effect is that one support person can handle the volume that used to need two or three, without hiring.',
  },
  {
    q: 'What is the best AI customer support software?',
    a: 'The best AI customer support software in 2026 depends on how AI is priced. Kora includes free AI ticket structuring and an AI Analyst (1 credit per answer) — pay-as-you-go credits ($1 = 10 credits, 1 credit per ticket, 1,100 free on signup, unlimited members). Kora AI drafts replies for your team (1 credit, $0.10, per draft) and can answer portal and dashboard tickets itself (3 credits, $0.30, per confirmed resolution). Intercom Fin charges $0.99 per outcome (fin.ai/pricing, checked 28 Oct 2026) and Zendesk $2.00 per automated resolution pay-as-you-go (zendesk.com/pricing, checked 28 Oct 2026). Freshdesk sells AI as a per-seat add-on. Kora has no seats and charges only when an answer is drafted or a customer confirms a resolution.',
  },
  {
    q: 'What does AI actually do in a ticketing system?',
    a: 'In a ticketing system, AI performs four concrete jobs: intake structuring (extracting title, category, and priority from a raw message), routing (assigning the ticket to the right team or person), reply drafting (generating a first response from your knowledge base for an agent to review), and analytics (answering questions like "what is our average resolution time this month?" without building reports).',
  },
];

const CAPABILITIES = [
  {
    capability: 'Ticket structuring',
    what: 'Turns a raw Slack thread, email, or WhatsApp message into a clean ticket with title, category, and priority.',
    example: 'Kora structures any pasted message with no extra charge.',
  },
  {
    capability: 'AI Copilot (reply drafting)',
    what: 'Drafts a reply from your own knowledge base and rules, with its sources shown, for an agent to edit and send.',
    example: 'Kora AI: Draft reply in the ticket. 1 credit per draft; no draft, no charge. Nothing is sent automatically.',
  },
  {
    capability: 'AI Agent (autonomous answers)',
    what: 'Answers portal and dashboard tickets from your own material, with citations.',
    example: 'Auto or Approval required. Billing, refunds, legal, security, upset customers and requests for a person always go to a human. 3 credits per confirmed resolution; escalations are free.',
  },
  {
    capability: 'AI Analyst (plain-language reporting)',
    what: 'Answers questions like "how many urgent tickets were resolved last month?" in seconds.',
    example: 'No SQL, no spreadsheets, no waiting on a data team. In Kora: 1 credit per answer.',
  },
];

export function AiForCustomerServicePage() {
  useEffect(() => {
    const title = 'AI for Customer Service — Examples and Costs (2026)';
    const description =
      'How AI is used in customer service in 2026: ticket structuring, reply drafting, autonomous answers and analytics, with an honest cost comparison.';
    setDocumentMeta(title, description);
    setCanonical('/ai-for-customer-service');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/ai-for-customer-service', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app' },
        { name: 'AI for Customer Service', url: 'https://usekora.app/ai-for-customer-service' },
      ]),
      buildTechArticleSchema(title, description, '2026-09-20'),
      buildFaqPageSchema(FAQS),
    ]);
    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Guide · Updated September 2026"
      title="AI for customer service"
      subtitle="What AI actually does in customer service tooling — with examples, results, and an honest look at costs."
      answer={
        <p>
          AI for customer service in 2026 does four concrete jobs: it <strong>structures</strong>{' '}
          incoming requests (turning emails, Slack threads, and chat messages into categorized,
          prioritized tickets), <strong>drafts replies</strong> for agents to review,{' '}
          <strong>answers repetitive questions</strong> autonomously, and{' '}
          <strong>analyzes ticket data</strong> in plain language. The main cost models are
          per-resolution metering (Intercom lists $0.99 per Fin outcome), per-seat AI
          add-ons (Zendesk, Freshdesk), and usage-based pricing (Kora: pay-as-you-go credits — $1 = 10 credits,
          1 credit per ticket, free AI structuring). The practical benefit for small teams is handling
          more volume without proportionally more headcount.
        </p>
      }
    >
      <ContentSection title="What AI for customer service means">
        <p>
          The term covers a lot of products, but in practice AI in customer service comes down to
          the jobs that used to consume agent time at intake and follow-up. Before AI, a support
          person receiving a messy email had to read it, invent a title, pick a category, guess the
          priority, and start writing a reply. AI now does that first pass.
        </p>
        <p>
          The result is not "replacing support teams" — it is removing the clerical layer so people
          handle judgment calls. Teams using AI intake typically see faster first responses and
          fewer misrouted tickets.
        </p>
      </ContentSection>

      <ContentSection title="The four jobs AI does today">
        <div className="rounded-xl border border-zinc-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Capability</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">What it does</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-zinc-500">Example</th>
              </tr>
            </thead>
            <tbody>
              {CAPABILITIES.map((c) => (
                <tr key={c.capability} className="border-b border-zinc-100 last:border-0">
                  <td className="px-4 py-3 font-medium text-zinc-900 whitespace-nowrap"><FeatureLabel label={c.capability} /></td>
                  <td className="px-4 py-3 text-zinc-500 ">{c.what}</td>
                  <td className="px-4 py-3 text-zinc-500 ">{c.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      <ContentSection title="What it costs — three pricing models">
        <p>
          <strong>Per-resolution metering.</strong> Some vendors bill per AI resolution — around{' '}
          $0.99 per resolution on top of seat pricing. At 1,000 resolutions per month that is
          roughly $990/month in AI fees alone. This model punishes exactly the teams that benefit
          most from AI: those with high repetitive volume.
        </p>
        <p>
          <strong>Per-seat AI add-ons.</strong> Legacy helpdesks commonly sell their AI assistant
          as a paid add-on layered on per-agent plans. Costs scale with headcount rather than
          value delivered.
        </p>
        <p>
          <strong>Included in usage pricing.</strong> A small set of tools — Kora included — do not charge
          extra for AI intake. Kora starts you off with 1,100 free credits and free AI ticket
          structuring; credits cost $1 for 10 and a ticket uses just 1, and the AI Analyst costs 1
          credit per answer. For budgeting, this means your AI structuring bill is zero regardless of volume.
        </p>
        <p>
          See our detailed comparisons for how this plays out per vendor:{' '}
          <Link to="/compare/kora-vs-intercom" className="underline hover:text-zinc-900 ">Kora vs Intercom</Link>,{' '}
          <Link to="/compare/kora-vs-zendesk" className="underline hover:text-zinc-900 ">Kora vs Zendesk</Link>, and{' '}
          <Link to="/compare/kora-vs-freshdesk" className="underline hover:text-zinc-900 ">Kora vs Freshdesk</Link>.
        </p>
      </ContentSection>

      <ContentSection title="What AI does not do">
        <p>
          Honest limits: AI does not know your product better than your best agent, it can
          confidently answer wrongly on questions outside your knowledge base, and it should not
          send sensitive or billing-affecting replies without review. The tools that work best in
          practice keep a human approval step on outbound replies and use AI for intake,
          drafting, and analysis — not unchecked autonomous communication.
        </p>
      </ContentSection>

      <ContentSection title="Choosing AI customer service software">
        <p>
          Three questions decide it: Is the AI included or metered? Does pricing scale
          per seat or per ticket? And can it handle your real intake channels — email, Slack, WhatsApp
          threads — or only clean web-form input? A 14-day trial with your actual ticket volume
          answers more than any feature list. Kora's 1,100 free credits (unlimited members, free AI ticket
          structuring) are enough to test that with real data.
        </p>
      </ContentSection>

      <ContentFaq faqs={FAQS} subtitle="Questions people ask about AI in customer service." />
    </ContentPage>
  );
}
